import { apiClient } from './client';

/**
 * drone-flights.api.ts — Cliente REST para vuelos de dron (F6).
 *
 * Alineado con aws-serverless/src/models/drone-flight.model.ts
 */

export const FLIGHT_TYPES = ['VIDEO', 'SCOUTING', 'APPLICATION'] as const;
export type FlightType = (typeof FLIGHT_TYPES)[number];

export const FLIGHT_STATUSES = ['DRAFT', 'CONFIRMED'] as const;
export type FlightStatus = (typeof FLIGHT_STATUSES)[number];

export interface DroneFlight {
  flightId: string;
  tenantId: string;
  deviceId?: string;
  flightType: FlightType;
  flightDate: string;
  loteId: string;
  moduleId?: string;
  valveCodes?: string[];
  videoS3Key?: string;
  thumbnailS3Key?: string;
  durationSec?: number;
  fileSizeBytes?: number;
  activityId?: string;
  areaCoveredHa?: number;
  litersApplied?: number;
  /** Telemetría del registro del fabricante (aeronave, baterías, velocidad, altura, separación de pasadas). */
  telemetry?: { aircraft: string; startBattery: number; endBattery: number; speedMs?: number; heightM?: number; rowSpacingM?: number };
  status: FlightStatus;
  notes?: string;
  recordedBy: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateDroneFlightInput {
  flightType: FlightType;
  flightDate?: string;
  loteId: string;
  moduleId?: string;
  valveCodes?: string[];
  deviceId?: string;
  activityId?: string;
  areaCoveredHa?: number;
  litersApplied?: number;
  notes?: string;
}

export interface UploadUrlInput {
  contentType: string;
  fileSizeBytes: number;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  videoS3Key: string;
}

export interface ConfirmFlightInput {
  videoS3Key?: string;
  thumbnailS3Key?: string;
  durationSec?: number;
  fileSizeBytes?: number;
}

/**
 * Listar vuelos de dron del tenant actual.
 * @param loteId Opcional: filtrar por lote
 * @param moduleId Opcional: filtrar por módulo
 */
export const fetchDroneFlights = async (params?: {
  loteId?: string;
  moduleId?: string;
  ranchoId?: string | null;
}): Promise<DroneFlight[]> =>
  (await apiClient.get('/api/drone-flights', {
    params: params ? { ...params, ranchoId: params.ranchoId || undefined } : undefined,
  })).data.items ?? [];

/**
 * Obtener un vuelo específico.
 */
export const fetchDroneFlight = async (flightId: string): Promise<DroneFlight> =>
  (await apiClient.get(`/api/drone-flights/${flightId}`)).data;

/**
 * Crear un nuevo vuelo (DRAFT).
 */
export const createDroneFlight = async (input: CreateDroneFlightInput): Promise<DroneFlight> =>
  (await apiClient.post('/api/drone-flights', input)).data;

/**
 * Obtener una URL presigned para subir el video directo a S3.
 * Valida MIME type y tamaño ANTES de firmar.
 */
export const getDroneFlightUploadUrl = async (
  flightId: string,
  input: UploadUrlInput,
): Promise<UploadUrlResponse> =>
  (await apiClient.post(`/api/drone-flights/${flightId}/upload-url`, input)).data;

/**
 * Confirmar un vuelo (DRAFT → CONFIRMED).
 * Requiere que el video ya haya llegado a S3.
 */
export const confirmDroneFlight = async (
  flightId: string,
  input: ConfirmFlightInput,
): Promise<DroneFlight> =>
  (await apiClient.put(`/api/drone-flights/${flightId}/confirm`, input)).data;

/**
 * Obtener una URL presigned (24h) para reproducir el video ya subido.
 */
export const fetchDroneFlightPlaybackUrl = async (flightId: string): Promise<{ url: string }> =>
  (await apiClient.get(`/api/drone-flights/${flightId}/playback-url`)).data;

// ── Importación desde Excel ───────────────────────────────────────────────────

export interface DroneImportPreviewRow {
  _row: number;
  flightType: FlightType;
  flightDate: string;
  loteName: string;
  valveCodes?: string[];
  areaCoveredHa?: number;
  litersApplied?: number;
  notes?: string;
}

export interface DroneImportError { _row: number; field: string; message: string }

export interface DroneImportResponse {
  dryRun?: boolean;
  errors: DroneImportError[];
  summary: { total: number; toCreate: number; created: number; skipped?: number };
  preview?: DroneImportPreviewRow[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function runDroneFlightImport(file: File, dryRun: boolean): Promise<DroneImportResponse> {
  const fileBase64 = await fileToBase64(file);
  return (await apiClient.post<DroneImportResponse>('/api/drone-flights/import', { fileBase64, dryRun })).data;
}

/** Valida el Excel sin escribir: devuelve preview y errores por fila. */
export const previewDroneFlightImport = (file: File) => runDroneFlightImport(file, true);
/** Crea los vuelos; el backend responde 422 si el archivo tiene errores. */
export const commitDroneFlightImport = (file: File) => runDroneFlightImport(file, false);
